import { classNames as cn } from "primereact/utils";
import React, { ReactNode, useMemo } from "react";

import { emptyStateImages, WSEmptyStateType } from "../../emptyStates";
import { WSOrientation } from "../../types/utils";
import { WSText } from "../core/WSText/WSText.component";
import { WSActions, WSActionsProps } from "../WSActions/WSActions";
import { WSElement, WSElementProps } from "../WSElement/WSElement.component";

import styles from "./WSEmptyState.module.scss";

export interface WSEmptyStateProps extends WSElementProps {
  type: WSEmptyStateType;
  title: string;
  description?: string | ReactNode;
  className?: string;
  actions?: WSActionsProps;
  orientation?: WSOrientation;
  size?: "S" | "M";
  hasBorder?: boolean;
  bgColor?: "transparent" | "white" | "gray";
  theme?: "neutral" | "garnet";
}

export const WSEmptyState: React.FC<WSEmptyStateProps> = ({
  type,
  orientation = "vertical",
  hasBorder,
  bgColor = "transparent",
  theme = "neutral",
  size = "M",
  title,
  description,
  actions,
  className,
  ...otherProps
}) => {
  const SVG = emptyStateImages[theme][type];

  const actionsProps = useMemo((): WSActionsProps | undefined => {
    if (!actions) return undefined;

    return {
      orientation,
      ...actions
    };
  }, [actions, orientation]);

  return (
    <WSElement
      className={cn(
        styles.emptyState,
        styles[`orientation-${orientation}`],
        styles[`bg-${bgColor}`],
        styles[`size-${size}`],
        {
          [styles.border]: hasBorder
        },
        className
      )}
      {...otherProps}
    >
      <SVG className={styles[`img-${size}`]} />

      <WSElement className={styles.textContent}>
        <WSText
          kind={size === "M" ? "Heading5" : "Paragraph"}
          className={styles.wordBreak}
          mb="XS"
          color="gray600"
          weight="medium"
        >
          {title}
        </WSText>

        {description && (
          <WSText.ParagraphSm
            className={styles.description}
            align={orientation === "vertical" ? "center" : "left"}
            color="gray600"
          >
            {description}
          </WSText.ParagraphSm>
        )}

        {!!actionsProps && (
          <WSActions mt="L" alignment="left" {...actionsProps} />
        )}
      </WSElement>
    </WSElement>
  );
};
